import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { FaMapMarkerAlt } from 'react-icons/fa';
import { Page, PageHeader, Card, Badge, EmptyRow, Alert, Loading, auditStatus, bookingStatus, formatDate, initials } from '../../ui';
import { formatAddress } from '../../ui/JobParts';
import { api } from '../../ui/api';

const History = ({ title, rows, idKey, statusOf, path }) => (
  <Card title={title} hint={rows ? `${rows.length} in total` : undefined} flush>
    {!rows ? <Loading /> : (
      <div className="table-wrap">
        <table className="table stack-sm">
          <thead><tr><th>ID</th><th>Service</th><th>Date</th><th>Status</th></tr></thead>
          <tbody>
            {rows.length ? rows.map((r) => {
              const s = statusOf(r.status_code);
              return (
                <tr key={r.id}>
                  <td data-label="ID" className="mono"><Link to={`${path}/${r.id}`}>{r[idKey]}</Link></td>
                  <td data-label="Service">{r.service ? r.service.service_name : '—'}</td>
                  <td data-label="Date" className="nowrap">{r.date}</td>
                  <td data-label="Status"><Badge tone={s.tone}>{s.label}</Badge></td>
                </tr>
              );
            }) : <EmptyRow cols={4}>None yet.</EmptyRow>}
          </tbody>
        </table>
      </div>
    )}
  </Card>
);

const UserDetails = () => {
  const { userId } = useParams();
  const [user, setUser] = useState(null);
  const [audits, setAudits] = useState(null);
  const [bookings, setBookings] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api(`/user/${userId}`).then((d) => (d.user ? setUser(d.user) : setError(d.message || 'Customer not found.'))).catch((e) => setError(e.message));
    api(`/audit/audits/${userId}`).then((d) => setAudits(d.audits || [])).catch(() => setAudits([]));
    api(`/booking/get_bookings_by_user/${userId}`).then((d) => setBookings(Array.isArray(d) ? d : [])).catch(() => setBookings([]));
  }, [userId]);

  if (error) return <Page><Alert>{error}</Alert></Page>;
  if (!user) return <Page><Loading /></Page>;

  return (
    <Page>
      <PageHeader title={user.username} subtitle={`Customer since ${formatDate(user.created_at)}`} back={{ to: '/manage-users', label: 'Customers' }} />
      <div className="grid grid-sidebar">
        <div className="stack">
          <History title="Audits" rows={audits} idKey="audit_id" statusOf={auditStatus} path="/audits/manage-audit" />
          <History title="Bookings" rows={bookings} idKey="booking_id" statusOf={bookingStatus} path="/bookings/manage-booking" />
        </div>
        <div className="stack">
          <Card title="Contact">
            <div className="row" style={{ flexWrap: 'nowrap', gap: 12, marginBottom: 16 }}>
              <div className="avatar lg">{initials(user.username)}</div>
              <div style={{ minWidth: 0 }}>
                <div className="strong">{user.username}</div>
                <div className="small muted">Customer</div>
              </div>
            </div>
            <dl className="kv one">
              <div><dt>Phone</dt><dd>{user.phone_number}</dd></div>
              <div><dt>Email</dt><dd>{user.email || '—'}</dd></div>
            </dl>
          </Card>
          <Card title="Addresses">
            {user.addresses && user.addresses.length ? (
              <ul className="list">
                {user.addresses.map((a) => (
                  <li key={a.id} style={{ alignItems: 'flex-start' }}>
                    <FaMapMarkerAlt style={{ color: 'var(--brand)', marginTop: 3, flexShrink: 0 }} />
                    <span style={{ flex: 1 }}>{formatAddress(a)}</span>
                    {a.is_primary && <Badge tone="green">Primary</Badge>}
                  </li>
                ))}
              </ul>
            ) : <p className="muted">No addresses saved.</p>}
          </Card>
        </div>
      </div>
    </Page>
  );
};

export default UserDetails;
